import { useQueryClient } from "@tanstack/react-query";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import TrainTravelClassDropdown from "components/dropdowns/reactselect/TrainTravelClassDropdown";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { GooglePlaceInputValue } from "components/ValidatedFields/GooglePlaceInput";
import { GooglePlaceInputWithValue } from "components/ValidatedFields/GoolgePlaceInputWithValue";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useCallback, useEffect } from "react";
import { invalidateTrainsCache } from "utilities/api/trainQuery";
import { getTrainTravelClass } from "utilities/enumutils";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenTrainSidePanel } from "./TrainSidePanel";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";

interface TrainNewInfo {
  artist: server.dto.ArtistSmall | null
  travellers: server.dto.ContactSmallV2[]

  bookingReference: string
  travelAgency: string
  notes: string
  trainNumber: string
  carriage: string
  seat: string
  travelClass: server.dto.TrainTravelClass

  departureStation: GooglePlaceInputValue
  departureDate: moment.Moment
  departurePlatform: string

  arrivalStation: GooglePlaceInputValue
  arrivalDate: moment.Moment
  arrivalPlatform: string
}

interface Props {
  fromTrain?: server.dto.TrainSidePanelData
}

const NewTrainSidePanel = (props: Props) => {
  const planningContext = React.useContext(PlanningContext);
  const openTrainSidePanel = useOpenTrainSidePanel();

  const [value, setValue] = React.useState<TrainNewInfo>({
    artist: props.fromTrain?.train.artist ?? planningContext.opts?.artist ?? null,
    travellers: props.fromTrain?.passengers ?? [],

    bookingReference: props.fromTrain?.train.bookingReference ?? "",
    travelAgency: props.fromTrain?.train.travelAgency ?? "",
    notes: props.fromTrain?.train.notes ?? "",
    trainNumber: "",
    carriage: "",
    seat: "",
    travelClass: props.fromTrain?.train.travelClass ? getTrainTravelClass(props.fromTrain?.train.travelClass) : null,

    departureStation: props.fromTrain?.train.arrival ? {
      googlePlaceId: props.fromTrain.train.arrival.googlePlaceId,
      placeIdentifier: props.fromTrain.train.arrival.placeIdentifier,
      name: props.fromTrain.train.arrival.name,
      address: props.fromTrain.train.arrival.address.formatted.join(", "),
      geo: props.fromTrain.train.arrival.latLng,
    } : null,
    departureDate: props.fromTrain?.train.dateTime.endDisplay ?
      moment.utc(props.fromTrain.train.dateTime.endDisplay) :
      (planningContext.opts?.date ?? moment.utc()),
    departurePlatform: props.fromTrain?.train.arrivalPlatform ?? "",

    arrivalStation: null,
    arrivalDate: props.fromTrain?.train.dateTime.endDisplay ?
      moment.utc(props.fromTrain.train.dateTime.endDisplay) :
      (planningContext.opts?.date ?? moment.utc()),
    arrivalPlatform: "",
  });

  const queryClient = useQueryClient();

  const submit = () => {
    const instruction: server.dto.CreateTrainInstruction = {
      arrivalGooglePlaceId: value.arrivalStation.googlePlaceId,
      arrivalLocal: value.arrivalDate.toDate(),
      arrivalPlatform: value.arrivalPlatform || "",
      artistId: value.artist?.artistId || null,
      bookingReference: value.bookingReference,
      carriage: value.carriage,
      departureGooglePlaceId: value.departureStation.googlePlaceId,
      departureLocal: value.departureDate.toDate(),
      departurePlatform: value.departurePlatform || "",
      linkToShowId: planningContext.opts?.fromShowId || null,
      notes: value.notes,
      passengers: value.travellers.map(t => t.contactId),
      seats: value.seat,
      trainNumber: value.trainNumber,
      travelAgency: value.travelAgency,
      travelClass: value.travelClass?.value ?? "unknown",
    }

    return window.CoreApi.Train.CreateTrain(instruction);
  }

  useEffect(() => {
    const fetchTravellers = async () => {
      if (value.artist?.artistId) {
        const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
          companyId: value.artist.artistId,
          defaultSelect: true,
          isActive: true,
          page: 1,
          perPage: 1000,
        });

        if (result.data.hasErrors) {
          return;
        }

        onChange({
          travellers: result.data.data.map(c => ({
            contactId: c.contact.contactId,
            culture: c.contact.culture,
            gender: c.contact.gender,
            isValidated: c.contact.isValidated,
            name: c.contact.name,
            position: c.contact.position,
            profileImageUrl: c.contact.profileImageUrl,
          }))
        });
      }
    }
    fetchTravellers();
  }, [value.artist?.artistId])

  const onChange = (newData: Partial<TrainNewInfo>) => {
    setValue({ ...value, ...newData });
  };

  return <SidePanelNewSectionTemplate
    afterCreate="displayItemInSidebar"
    onSave={id => {
      invalidateTrainsCache(queryClient);
      openTrainSidePanel(id);
    }}
    title={RESX.Train.NewTrainJourney}
    id="new-train-side-panel"
    trigger={null}
    submit={submit}
    fields={[{
      key: "artist",
      label: site.currentEnvironment.functionGroups.artist.singular,
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          securityScope="limitByUserAccess"
          canCreateNew={false}
          nonInPageEditing={{
            onChange: artist => onChange({ artist }),
            value: value.artist,
            validatorKey: "artist",
            required: true,
          }}
        />
      </div>
    }, {
      key: "travellers",
      label: RESX.Flight.Travellers,
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          value={value.travellers}
          onChange={travellers => onChange({ travellers })}
          canCreateNew={false}
          required={false}
          validatorKey="contacts"
        />
      </div>
    }, {
      key: "bookingReference",
      label: RESX.Flight.BookingCode,
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.train.maxBookingCodeLength}
        value={value.bookingReference}
        onChange={bookingReference => onChange({ bookingReference })}
      />
    }, {
      key: "travelAgency",
      label: RESX.Flight.TravelAgency,
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.train.maxTravelAgencyLength}
        value={value.travelAgency}
        onChange={travelAgency => onChange({ travelAgency })}
      />
    }, {
      key: "notes",
      label: RESX.Note.Notes,
      renderEditNew: () => <OptionalTextAreaInput
        maxLength={serverReference.constants.groundTransport.maxNotesLength}
        rows={3}
        value={value.notes}
        onChange={notes => onChange({ notes })}
      />
    }, {
      key: "trainNumber",
      label: RESX.Train.TrainNumber,
      renderEditNew: () => <OptionalTextInput
        width={100}
        maxLength={serverReference.constants.train.maxTrainNumberLength}
        value={value.trainNumber}
        onChange={trainNumber => onChange({ trainNumber })}
      />
    }, {
      key: "carriage",
      label: RESX.Train.Carriage,
      renderEditNew: () => <OptionalTextInput
        width={100}
        maxLength={serverReference.constants.train.maxCarriageLength}
        value={value.carriage}
        onChange={carriage => onChange({ carriage })}
      />
    }, {
      key: "seat",
      label: RESX.Flight.Seats,
      renderEditNew: () => <OptionalTextInput
        width={200}
        maxLength={serverReference.constants.train.maxSeatsLength}
        value={value.seat}
        onChange={seat => onChange({ seat })}
      />
    }, {
      key: "travelClass",
      label: RESX.Flight.TravelClass,
      renderEditNew: () => <TrainTravelClassDropdown
        value={value.travelClass}
        onChange={travelClass => onChange({ travelClass })}
      />
    }, {
      key: "departureArrivalLabel",
      label: "",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Departure}</h3>
        <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Arrival}</h3>
      </div>
    }, {
      key: "date",
      label: RESX.DateTime.lblDate,
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <DateTimePicker
          value={value.departureDate}
          onChange={departureDate => {
            const diff = departureDate.diff(value.departureDate, "minutes");
            const newArrivalDate = value.arrivalDate.clone().add(diff, "minutes");
            onChange({ departureDate, arrivalDate: newArrivalDate })
          }}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
        />
        <DateTimePicker
          value={value.arrivalDate}
          onChange={arrivalDate => onChange({ arrivalDate })}
          validatorKey="arrivalDate"
          minDate={value.departureDate.clone().add(serverReference.constants.train.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={value.departureDate.clone().add(serverReference.constants.train.maxArrivalAfterDepartureHours, "hours")}
        />
      </div>
    }, {
      key: "station",
      label: RESX.Train.Station,
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <GooglePlaceInputWithValue
          isRequired
          validatorKey="departure-station"
          value={value.departureStation}
          calledFrom="train"
          placeType={"trainStation"}
          onPlaceSelected={(v: GooglePlaceInputValue) => {
            onChange({ departureStation: v });
          }}
        />
        <GooglePlaceInputWithValue
          isRequired
          validatorKey="arrival-station"
          value={value.arrivalStation}
          calledFrom="train"
          placeType={"trainStation"}
          onPlaceSelected={(v: GooglePlaceInputValue) => {
            onChange({ arrivalStation: v });
          }}
        />
      </div>
    }, {
      key: "platform",
      label: RESX.Train.Platform,
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <OptionalTextInput
          width={60}
          value={value.departurePlatform}
          onChange={departurePlatform => onChange({ departurePlatform })}
          maxLength={serverReference.constants.train.maxPlatformLength}
        />
        <OptionalTextInput
          width={60}
          value={value.arrivalPlatform}
          onChange={arrivalPlatform => onChange({ arrivalPlatform })}
          maxLength={serverReference.constants.train.maxPlatformLength}
        />
      </div>
    }]}
  />
}

export const useOpenNewTrainSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewTrainSidePanel = useCallback(async (addingSegmentFromTrainId?: number) => {
    let fromTrain: server.dto.TrainSidePanelData = undefined;
    if (addingSegmentFromTrainId) {
      const result = await window.CoreApi.Train.GetSidePanelData(addingSegmentFromTrainId);
      fromTrain = result.data.data;
    }
    openSidePanel(
      <NewTrainSidePanel fromTrain={fromTrain} />,
      "new-train-side-panel",
      "large",
    );
  }, [openSidePanel]);

  return openNewTrainSidePanel;
}